import { useEffect, useRef } from "react";

interface MarqueeProps {
  content: string;
  speed: number;
  height: number;
  direction?: "left" | "right";
  fontFamily?: string;
  fontColor?: string;
  fontSize?: number;
}

const Marquee = ({
  content,
  speed,
  height,
  direction,
  fontFamily,
  fontColor,
  fontSize,
}: MarqueeProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<Animation | null>(null);

  const startMarquee = () => {
    if (!containerRef.current || !textRef.current) return;
    if (animationRef.current) animationRef.current.cancel();

    const containerWidth = containerRef.current.offsetWidth;
    const textWidth = textRef.current.offsetWidth;
    const totalDistance = containerWidth + textWidth;
    const duration = (totalDistance / speed) * 100;

    let keyframes: Keyframe[];

    if (direction === "left") {
      // Start from right edge, move to left until fully hidden
      keyframes = [
        { transform: `translateX(${containerWidth}px)` },
        { transform: `translateX(-${textWidth}px)` },
      ];
    } else {
      // Start from left edge (hidden), move to right until fully hidden
      keyframes = [
        { transform: `translateX(-${textWidth}px)` },
        { transform: `translateX(${containerWidth}px)` },
      ];
    }

    animationRef.current = textRef.current.animate(keyframes, {
      duration,
      iterations: Infinity,
      easing: "linear",
    });
  };

  useEffect(() => {
    startMarquee();
    window.addEventListener("resize", startMarquee);
    return () => {
      if (animationRef.current) animationRef.current.cancel();
      window.removeEventListener("resize", startMarquee);
    };
  }, [content, speed, height, direction]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        overflow: "hidden",
        whiteSpace: "nowrap",
        height: `${height}px`,
        display: "flex",
        alignItems: "center",
        background: "#000",
      }}
    >
      <div
        ref={textRef}
        style={{
          position: "absolute",
          left: 0,
          display: "inline-block",
          lineHeight: 1,
          willChange: "transform",
        }}
      >
        <p style={{ margin: 0 }}>
          <span
            style={{
              fontFamily: fontFamily,
              color: fontColor,
              fontSize: `${fontSize}px`,
            }}
          >
            {content}
          </span>
        </p>
      </div>
    </div>
  );
};

export default Marquee;
